import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate, CR } from "../lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList } from "recharts";
import { Target } from "lucide-react";

const STATUS_COLOR = { "In Progress": "#fb8b1e", "Planned": "#8892B0", "Under Negotiation": "#0068ff", "Legal Notice Sent": "#ff433d", "Rollout": "#4af6c3", "Valuation In Progress": "#fb8b1e", "Investor Meetings": "#0068ff", "Documents Filed": "#4af6c3" };

export default function Growth() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/growth/roadmap").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING ROADMAP...</div>;

    const chartData = data.roadmap.map((r) => ({ quarter: r.quarter, target: r.target / CR, actual: r.actual ? r.actual / CR : null }));

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// STRATEGIC GROWTH</div>
                <h1 className="font-display font-black text-3xl">₹15 Cr → ₹100 Cr Roadmap</h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">18-Month Vision · 5 Quarters · 10 Initiatives</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="growth-current" label="Current Run-rate" value={15.5 * CR} sub="FY 25-26 annual" />
                <KpiCard testId="growth-target" label="Target" value={100 * CR} sub="Q4 FY 26-27" accent />
                <KpiCard testId="growth-gap" label="Gap to Close" value={84.5 * CR} sub="6.5x growth" />
                <KpiCard testId="growth-init" label="Active Initiatives" value={data.initiatives.length} format="custom" sub="Across teams" />
            </div>

            <Panel testId="growth-chart" title="Quarterly Turnover Trajectory (₹ Cr)">
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                            <CartesianGrid stroke="#E2E8F0" vertical={false} strokeOpacity={0.5} />
                            <XAxis dataKey="quarter" tick={{ fill: "#8892B0", fontSize: 10 }} />
                            <YAxis tick={{ fill: "#8892B0", fontSize: 10 }} unit=" Cr" />
                            <Tooltip formatter={(v) => `₹${v} Cr`} />
                            <Bar dataKey="target" fill="#fb8b1e" name="Target">
                                <LabelList dataKey="target" position="top" fill="#fb8b1e" fontSize={10} formatter={(v) => `₹${v}`} />
                            </Bar>
                            <Bar dataKey="actual" fill="#4af6c3" name="Actual">
                                {chartData.map((_, i) => <Cell key={i} fill="#4af6c3" />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Panel>

            <Panel testId="growth-initiatives" title="Growth Initiatives" subtitle={`${data.initiatives.length} tracked · Owners across MD, CFO, PMs, HR`}>
                <div className="space-y-2">
                    {data.initiatives.map((i, idx) => (
                        <div key={idx} className="border border-panel p-3 hover:bg-[var(--bg-panel-hover)] transition-colors flex items-start gap-3">
                            <div className="w-8 h-8 border border-[var(--brand-amber)] flex items-center justify-center shrink-0">
                                <Target size={14} className="text-[var(--brand-amber)]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-[var(--text-primary)] font-body">{i.initiative}</div>
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-1">
                                    Owner: <span className="text-[var(--text-primary)]">{i.owner}</span> · Impact: <span className="text-pos">{i.impact}</span>
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <span className="text-[10px] font-mono-fin uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: STATUS_COLOR[i.status] || "#E2E8F0", color: STATUS_COLOR[i.status] || "#8892B0" }}>{i.status}</span>
                                <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider mt-1">Due {fmtDate(i.due)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>
        </div>
    );
}
